import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { IdName, PriceMatrixList, PriceMatrixSalesRepType } from '../../../../../interfaces/pricematrixmodel';
import { LookUpService } from '../../../../../services/lookupservice';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { AlertDialogComponent } from '../../../../shared/components/alert-dialog/alert-dialog.component';
import { formatDateOnly, handleDateKeydown, numericInputLimit } from '../../../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-addedit-pricematrix-salesrep',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatDialogModule,
    DragDropModule
  ],
  templateUrl: './addedit-pricematrix-salesrep.component.html',
  styleUrl: './addedit-pricematrix-salesrep.component.scss'
})
export class AddeditPricematrixSalesrepComponent {
  private dialogRef = inject(MatDialogRef<AddeditPricematrixSalesrepComponent>);
  private data: PriceMatrixSalesRepType = inject(MAT_DIALOG_DATA);
  private lookup: LookUpService = inject(LookUpService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);

  // Component State
  form!: FormGroup;
  salesReps = signal<IdName[]>([]);  
  filteredSalesReps = signal<IdName[]>([]);
  isEditMode = signal(false);
  dialogTitle = signal('');
  displayValue = signal<string>('');

  constructor() {
    console.log("AddEditPriceMatrixSalesRep Dialog data:", this.data);
  }

  ngOnInit(): void {
    this.loaddropdowns();
    const defaultData: Partial<PriceMatrixSalesRepType> = {
      task: 'Add',
      id: 0,
      priceMatrixId: this.data.priceMatrixId || 0,
      salesRepId: null,
      salesRepName: '',
      effectiveStartDate: null,
      effectiveEndDate: null,
      commEligible: false,
      percentage: null
    };

    // Merge injected data with defaults
    const initialData = { ...defaultData, ...this.data };

    this.isEditMode.set(initialData.task === 'Edit');
    this.dialogTitle.set(this.isEditMode() ? `Edit Sales Rep: ${initialData.salesRepName}` : 'Add Sales Rep');

    // Initialize Reactive Form with all fields mandatory
    this.form = this.fb.group({
      salesRepId: [initialData.salesRepId, Validators.required],
      effectiveStartDate: [initialData.effectiveStartDate, Validators.required],
      effectiveEndDate: [initialData.effectiveEndDate],
      commEligible: [initialData.commEligible],
      percentage: [initialData.percentage, [Validators.required, Validators.min(0), Validators.max(100)]],
      
      // Hidden/Contextual fields
      id: [initialData.id],
      priceMatrixId: [initialData.priceMatrixId, Validators.required],
    });

    // Handle initial selection for Rental Type Name/Size based on ID if needed
    if (initialData.salesRepId) {
      const selectedType = this.salesReps().find(t => t.id === initialData.salesRepId);
      if (selectedType) {
          // Optional: You might need to update a rentalTypeName field if it exists
      }
    }
  }

  loaddropdowns(): void {
    this.lookup.getSalesRep().subscribe(types => {
      this.salesReps.set(types);
      this.filteredSalesReps.set(types);
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.error("Form is invalid. Cannot save.");
      return;
    }
    
    const formValue: PriceMatrixSalesRepType = this.form.value;
    const selectedType = this.salesReps().find(t => t.id === formValue.salesRepId);
    
    const result: PriceMatrixSalesRepType = {
        ...formValue,
        effectiveEndDate: formValue.effectiveEndDate === null ? null : formatDateOnly(formValue.effectiveEndDate),
        effectiveStartDate: formValue.effectiveStartDate ? formatDateOnly(formValue.effectiveStartDate) : null,
        task: this.isEditMode() ? 'Edit' : 'Add',
        salesRepName: selectedType ? selectedType.name : 'Unknown',
    };

    let startDate = formValue.effectiveStartDate ? new Date(formValue.effectiveStartDate) : null;
    let endDate = formValue.effectiveEndDate ? new Date(formValue.effectiveEndDate) : null;
    if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'End date should be after Start date.'
        },
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    console.log("Saving Sales Rep:", result);

    this.dialogRef.close(result);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  filterSalesReps(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredSalesReps.set(this.salesReps());
    } else {
      this.filteredSalesReps.set(this.salesReps().filter(
        rep => rep.name.toLowerCase().includes(searchTerm)
      ));
    }
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.form.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.form.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.form.get(controlName)?.setValue(parsedValue , { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }
  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
  }
  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
